import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import { useApplyModal } from './ApplyModalContext';
import { MessageSquareIcon, BarChart2Icon, UsersIcon, SparklesIcon, ActivityIcon, HeartIcon, Share2Icon, AtSignIcon } from 'lucide-react';
const inputs = [{
  text: 'fan comments',
  icon: MessageSquareIcon,
  delay: 0,
  x: -150,
  y: -90
}, {
  text: 'DMs',
  icon: MessageSquareIcon,
  delay: 0.4,
  x: 140,
  y: -110
}, {
  text: 'recurring asks',
  icon: BarChart2Icon,
  delay: 0.8,
  x: -170,
  y: 30
}, {
  text: 'audience conviction',
  icon: HeartIcon,
  delay: 0.2,
  x: 160,
  y: 10
}, {
  text: 'content themes',
  icon: SparklesIcon,
  delay: 0.6,
  x: -110,
  y: 130
}, {
  text: 'fan signals',
  icon: Share2Icon,
  delay: 1.0,
  x: 120,
  y: 120
}, {
  text: 'replies',
  icon: MessageSquareIcon,
  delay: 0.3,
  x: 30,
  y: -150
}, {
  text: 'creator context',
  icon: UsersIcon,
  delay: 0.7,
  x: -50,
  y: 160
}, {
  text: 'audience conversations',
  icon: AtSignIcon,
  delay: 1.1,
  x: 190,
  y: 90
}, {
  text: 'what fans love',
  icon: HeartIcon,
  delay: 0.5,
  x: -200,
  y: -40
}];
export function Hero() {
  const {
    open
  } = useApplyModal();
  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section className="relative pt-28 pb-16 px-6 overflow-hidden flex items-center min-h-screen" data-id="element-77">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-electric/10 rounded-full blur-[120px] pointer-events-none" data-id="element-78" />

      <div className="relative z-10 w-full max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-12 items-center" data-id="element-79">
        {/* Copy */}
        <div className="order-2 lg:order-1 text-center lg:text-left max-w-xl mx-auto lg:mx-0" data-id="element-80">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tightest text-white mb-6" data-id="element-81">
            Uncover your next big move.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: 'easeOut'
        }} className="text-lg sm:text-xl text-slate-400 mb-8 leading-relaxed" data-id="element-82">
            Your AI creative collaborator that turns audience engagement into
            launch intelligence for new ideas uniquely you, that your fans will
            love.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: 'easeOut'
        }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8" data-id="element-83">
            <Button variant="primary" onClick={open} className="w-full sm:w-auto text-base py-3 px-6" data-id="element-84">
              Apply to the Creator Lab
            </Button>
            <Button variant="secondary" onClick={scrollToHowItWorks} className="w-full sm:w-auto text-base py-3 px-6" data-id="element-85">
              See how it works
            </Button>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="text-sm text-slate-500 max-w-xl mx-auto lg:mx-0" data-id="element-86">
            For creators ready to do more with the audience they built —
            content, products, memberships, merch, services, events,
            communities, and more.
          </motion.p>
        </div>

        {/* Abstract Visual */}
        <div className="order-1 lg:order-2 relative w-full h-[320px] sm:h-[400px] flex items-center justify-center" aria-label="Abstract visual showing fan signals and creator context coming together into a clear next move" data-id="element-87">
          {/* Floating Inputs */}
          {inputs.map((input, i) => {
          const Icon = input.icon;
          return <motion.div key={i} initial={{
            opacity: 0,
            x: input.x * 2,
            y: input.y * 2
          }} animate={{
            opacity: [0, 1, 0],
            x: [input.x * 1.5, 0],
            y: [input.y * 1.5, 0],
            scale: [1, 0.8, 0.5]
          }} transition={{
            duration: 5,
            delay: input.delay,
            repeat: Infinity,
            repeatDelay: 1.5,
            ease: 'easeInOut'
          }} className="absolute flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy-800/80 border border-white/10 text-xs text-slate-300 backdrop-blur-sm" data-id="element-88">
                <Icon className="w-3 h-3 text-electric" data-id="element-89" />
                {input.text}
              </motion.div>;
        })}

          {/* Central Core */}
          <motion.div animate={{
          boxShadow: ['0 0 20px rgba(79,141,255,0.2)', '0 0 60px rgba(79,141,255,0.6)', '0 0 20px rgba(79,141,255,0.2)']
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute w-24 h-24 rounded-full bg-gradient-to-br from-electric to-mint flex items-center justify-center z-20 blur-[2px]" data-id="element-90" />
          <div className="absolute w-20 h-20 rounded-full bg-navy-950 z-20 flex items-center justify-center border border-white/10" data-id="element-91">
            <ActivityIcon className="w-8 h-8 text-white" data-id="element-92" />
          </div>
        </div>
      </div>
    </section>;
}